/**
 * PackingList - Manage packing items for a bucket list item
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Package,
  Plus,
  Trash2,
  ChevronDown,
  Sparkles,
  RotateCcw,
  Shirt,
  Laptop,
  FileText,
  Briefcase,
  MoreHorizontal,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { BACKEND_URL as API_URL } from "@/config/api";

const CATEGORIES = [
  { id: "essentials", name: "Essentials", icon: Briefcase },
  { id: "clothing", name: "Clothing", icon: Shirt },
  { id: "toiletries", name: "Toiletries", icon: Package },
  { id: "electronics", name: "Electronics", icon: Laptop },
  { id: "documents", name: "Documents", icon: FileText },
  { id: "other", name: "Other", icon: MoreHorizontal },
];

const TEMPLATES = [
  { id: "essentials", name: "Travel Essentials" },
  { id: "beach", name: "Beach Trip" },
  { id: "city", name: "City Break" },
  { id: "adventure", name: "Adventure Trip" },
  { id: "winter", name: "Winter Trip" },
];

export const PackingList = ({ itemId, token }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, packed: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState("");
  const [newCategory, setNewCategory] = useState("essentials");
  const [expandedCategories, setExpandedCategories] = useState(["essentials", "clothing"]);

  const fetchPackingList = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/items/${itemId}/packing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data.items);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Failed to fetch packing list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (itemId && token) {
      fetchPackingList();
    }
  }, [itemId, token]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.trim()) return;

    try {
      await axios.post(
        `${API_URL}/api/items/${itemId}/packing`,
        { name: newItem.trim(), category: newCategory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewItem("");
      fetchPackingList();
      toast.success("Item added to packing list");
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  const handleTogglePacked = async (packingId, currentStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/items/${itemId}/packing/${packingId}`,
        { packed: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPackingList();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleDeleteItem = async (packingId) => {
    try {
      await axios.delete(`${API_URL}/api/items/${itemId}/packing/${packingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPackingList();
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleAddTemplate = async (templateId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/items/${itemId}/packing/template/${templateId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPackingList();
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to add template");
    }
  };

  const handleResetAll = async () => {
    try {
      await axios.post(
        `${API_URL}/api/items/${itemId}/packing/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPackingList();
      toast.success("All items reset to unpacked");
    } catch (error) {
      toast.error("Failed to reset items");
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((c) => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Group items by category
  const itemsByCategory = CATEGORIES.map((cat) => ({
    ...cat,
    items: items.filter((item) => item.category === cat.id),
  })).filter((cat) => cat.items.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-charcoal">Packing List</h3>
        </div>
        <div className="text-sm text-muted-foreground">
          {stats.packed}/{stats.total} packed ({stats.percentage}%)
        </div>
      </div>

      {/* Progress Bar */}
      {stats.total > 0 && (
        <div className="h-2 bg-charcoal/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
            style={{ width: `${stats.percentage}%` }}
          />
        </div>
      )}

      {/* Quick Templates */}
      <div className="flex flex-wrap gap-2">
        {TEMPLATES.map((template) => (
          <Button
            key={template.id}
            variant="outline"
            size="sm"
            onClick={() => handleAddTemplate(template.id)}
            className="h-8 text-xs"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            {template.name}
          </Button>
        ))}
      </div>

      {/* Add New Item */}
      <form onSubmit={handleAddItem} className="flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add item..."
          className="flex-1 h-10"
        />
        <Select value={newCategory} onValueChange={setNewCategory}>
          <SelectTrigger className="w-32 h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" size="icon" className="h-10 w-10">
          <Plus className="w-4 h-4" />
        </Button>
      </form>

      {/* Packing Items by Category */}
      <ScrollArea className="max-h-80">
        <div className="space-y-2">
          {itemsByCategory.map((category) => {
            const CategoryIcon = category.icon;
            const packedInCategory = category.items.filter((i) => i.packed).length;

            return (
              <Collapsible
                key={category.id}
                open={expandedCategories.includes(category.id)}
                onOpenChange={() => toggleCategory(category.id)}
              >
                <CollapsibleTrigger className="w-full flex items-center justify-between p-2 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{category.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({packedInCategory}/{category.items.length})
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      expandedCategories.includes(category.id) ? "rotate-180" : ""
                    }`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-1">
                  <div className="space-y-1 pl-2">
                    {category.items.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          item.packed ? "bg-green-50" : "hover:bg-muted/30"
                        }`}
                      >
                        <Checkbox
                          checked={item.packed}
                          onCheckedChange={() => handleTogglePacked(item.id, item.packed)}
                          className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                        />
                        <span
                          className={`flex-1 text-sm ${
                            item.packed ? "line-through text-muted-foreground" : "text-charcoal"
                          }`}
                        >
                          {item.name}
                          {item.quantity > 1 && (
                            <span className="text-xs text-muted-foreground ml-1">
                              (x{item.quantity})
                            </span>
                          )}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-red-500"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </ScrollArea>

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No items yet. Add items or use a template!</p>
        </div>
      )}

      {/* Reset Button */}
      {stats.packed > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetAll}
          className="w-full"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset All to Unpacked
        </Button>
      )}
    </div>
  );
};

export default PackingList;
